import React, { useState } from 'react';
import { List, Segmented, Spin, Modal, Space, Button, Flex, Typography, Tabs, Empty } from 'antd';
import { PlusOutlined, GlobalOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import ThemeEditorModal from './ThemeEditorModal';
import ThemePreviewCard from './ThemePreviewCard';
import ThemePublicModal from './ThemePublicModal';

import { 
    useGetThemesQuery,
    useCreateThemeMutation,
    useDeleteThemeMutation,
    useUpdateThemeMutation,
    useUpdatePublicThemeMutation,
} from '../store/user';

import { setPriorityNumberIcons, setTheme } from '../store/settings';

import { useSelector, useDispatch } from 'react-redux';
import { useConfirm } from '../hooks/useConfirm';

import type { RootState } from '../store';
import type { ThemeItem, ThemeConfig } from '../../types';

const { Paragraph } = Typography;

interface SettingsModalProps {
    open: boolean;
    onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const settings = useSelector((state: RootState) => state.settings);

    const { confirm, confirmModal } = useConfirm();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isPublicModalOpen, setIsPublicModalOpen] = useState(false);
    const [editingThemeId, setEditingThemeId] = useState<number | null>(null);
    const [editingThemeTheme, setEditingThemeTheme] = useState<ThemeConfig | null>(null);
    const [editingThemeName, setEditingThemeName] = useState<string>('');

    const { data: themes, isLoading, error } = useGetThemesQuery(undefined, { skip: !open });

    const [deleteTheme] = useDeleteThemeMutation();
    const [updateTheme] = useUpdateThemeMutation();
    const [createTheme] = useCreateThemeMutation();
    const [updatePublicTheme] = useUpdatePublicThemeMutation();

    const handleCreateNew = () => {
        setEditingThemeId(null);
        setEditingThemeTheme(null);
        setEditingThemeName('');
        setIsEditorOpen(true);
    };

    const handleDelete = async (theme: ThemeItem) => {
        const result = await confirm({
            message: `Вы уверены, что хотите удалить тему «${ theme.name }»?`,
        });

        if (result) {
            deleteTheme({ themeId: theme.themeId });
        }
    };

    const handleEdit = (theme: ThemeItem) => {
        if (theme.isDefault) {
            setEditingThemeId(null);
            setEditingThemeName(`${ theme.name } — копия`);
        }
        else {
            setEditingThemeId(theme.themeId);
            setEditingThemeName(theme.name);
        }

        setEditingThemeTheme(theme.theme);
        setIsEditorOpen(true);
    };

    const handleSaveTheme = async (name: string, theme: ThemeConfig, themeId: number | null) => {
        if (themeId) {
            await updateTheme({ name, theme, themeId });
        }
        else {
            await createTheme({ name, theme });
        }

        setIsEditorOpen(false);
    };

    const handleApplyTheme = async (theme: ThemeItem) => {
        const result = await confirm({
            message: `Применить тему «${ theme.name }»?`,
        });

        if (result) {
            dispatch(setTheme({ themeId: theme.themeId, theme: theme.theme }));
        }
    };

    const themesContent = (() => {
        if (isLoading) {
            return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;
        }

        if (error || !themes) {
            return <div className="app-empty">Не удалось загрузить темы</div>;
        }

        if (!themes.message.length) {
            return <Empty description="Пока нет сохранённых тем" />;
        }

        return (
            <List
                grid={{
                    gutter: 16,
                    xs: 1,
                    sm: 2,
                    md: 2,
                    lg: 3,
                    xl: 3,
                    xxl: 3,
                }}
                dataSource={themes.message}
                renderItem={(theme) => (
                    <List.Item>
                        <ThemePreviewCard
                            key={theme.themeId}
                            name={theme.name}
                            theme={theme.theme}
                            isDefault={theme.isDefault}
                            isPublic={theme.isPublic}
                            onEdit={() => handleEdit(theme)}
                            onDelete={() => handleDelete(theme)}
                            onUpdatePublic={() => updatePublicTheme({ themeId: theme.themeId, value: !theme.isPublic })}
                            onApply={() => handleApplyTheme(theme)}
                        />
                    </List.Item>
                )}
            />
        );
    })();

    return (
        <>
            <Modal
                title="Настройки"
                open={open}
                onCancel={onClose}
                footer={null}
                width={860}
                destroyOnHidden
            >
                <Tabs
                    items={[
                        {
                            key: 'themes',
                            label: 'Оформление',
                            children: (
                                <Flex vertical gap={16}>
                                    <Flex justify="space-between" align="center" wrap gap={12}>
                                        <Paragraph type="secondary" style={{ margin: 0, maxWidth: 480 }}>
                                            Выберите тему или создайте свою. ПКМ по карточке — дополнительные действия.
                                        </Paragraph>
                                        <Space>
                                            <Button
                                                icon={<GlobalOutlined />}
                                                onClick={() => setIsPublicModalOpen(true)}
                                            >
                                                Галерея
                                            </Button>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={handleCreateNew}
                                            >
                                                Новая тема
                                            </Button>
                                        </Space>
                                    </Flex>
                                    {themesContent}
                                </Flex>
                            ),
                        },
                        {
                            key: 'priority',
                            label: 'Приоритеты',
                            children: (
                                <Flex vertical gap={12}>
                                    <Paragraph type="secondary" style={{ margin: 0 }}>
                                        Как отображать приоритет на карточках задач
                                    </Paragraph>
                                    <Segmented
                                        value={settings.priorityNumberIcons}
                                        options={[
                                            { value: false, icon: <ExclamationCircleOutlined />, label: 'Иконки' },
                                            { value: true, label: 'Цифры' },
                                        ]}
                                        onChange={(val) => dispatch(setPriorityNumberIcons(val))}
                                    />
                                </Flex>
                            ),
                        },
                    ]}
                />

                {isEditorOpen && 
                    <ThemeEditorModal
                        open={isEditorOpen}
                        initialName={editingThemeName}
                        initialTheme={editingThemeTheme}
                        onSave={(name, theme) => handleSaveTheme(name, theme, editingThemeId)}
                        onCancel={() => {
                            setIsEditorOpen(false);
                        }}
                    />
                }
            </Modal>

            <ThemePublicModal
                open={isPublicModalOpen}
                onClose={() => setIsPublicModalOpen(false)}
            />

            {confirmModal}
        </>
    );
};

export default SettingsModal;
